import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useAuth as useClerkAuth, useUser as useClerkUser } from "@clerk/clerk-react";
import { api, setTokenGetter, setSignedInFlag, FORCE_SIGNOUT_EVENT } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

// Module-level idempotence gate for the "role changed → sign out" flow.
// Placed outside the component so the flag survives React re-mounts
// (StrictMode double-invocation, etc.) and is genuinely once-per-page-load.
const forceSignoutFiredRef = { current: false };

/**
 * Wraps Clerk's auth state and joins it with our backend user record (which
 * carries the `role`: "admin" | "executive"). Backend issues the role on
 * first sign-in (first user = admin, everyone else = executive).
 */
export function AuthProvider({ children }) {
  const { getToken, isLoaded, isSignedIn, signOut: clerkSignOut } = useClerkAuth();
  const { user: clerkUser } = useClerkUser();
  const [user, setUser] = useState(null);   // our backend user doc
  const [permissions, setPermissions] = useState(new Set()); // permission keys for current user
  // Tracks whether /permissions/catalog has RESOLVED for the current user
  // (success or failure). Consumers gating on `hasPerm()` MUST wait for
  // this to be true before deciding a user "doesn't have" a permission,
  // otherwise they'll trigger a redirect against the empty initial Set
  // and cause a Navigate → guard → Navigate loop that trips the browser's
  // history.replaceState throttle. See Sentry issue #508a82c3.
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const fetchingRef = useRef(false);
  // Track the Clerk user id the current in-memory user/permissions belong to.
  // If Clerk swaps to a different user (sign-out → sign-in-as-different-user
  // in the same page, or a UserButton account switch) we must NOT let the
  // previous user's `permissions` Set leak into the new session for even a
  // single render — that's a real privilege-escalation window.
  const activeClerkIdRef = useRef(null);

  // Wire api.js to ask Clerk for the JWT on every request, and tell it
  // whether we're signed in so the request interceptor can wait for the
  // token to be ready instead of firing without auth.
  useEffect(() => {
    setTokenGetter(() => getToken());
  }, [getToken]);

  useEffect(() => {
    setSignedInFlag(!!isSignedIn);
  }, [isSignedIn]);

  // ── Security-critical: nuke all cached identity state the moment the
  // Clerk user id changes or Clerk transitions to signed-out. Without
  // this, sign-out-then-sign-in-as-different-user leaves the previous
  // user's `permissions` Set in memory until the new /permissions/catalog
  // response lands (hundreds of ms). During that window `hasPerm()` would
  // grant User B access to features they don't own. Route guards
  // (AdminRoute, PermRoute) read the same stale state, so User B could
  // even land on Settings for a flash before the new perms arrive.
  //
  // Rule: `permissions`/`user` are ONLY ever kept if `activeClerkIdRef`
  // matches the current Clerk user id. Anything else → wipe.
  useEffect(() => {
    const nextId = isSignedIn ? clerkUser?.id ?? null : null;
    if (activeClerkIdRef.current !== nextId) {
      activeClerkIdRef.current = nextId;
      setUser(null);
      setPermissions(new Set());
      setPermissionsLoaded(false);
      // fetchingRef is guarded so an in-flight /auth/me for the previous
      // user doesn't overwrite the freshly-cleared state. Reset it here.
      fetchingRef.current = false;
      // Also flip loading back to true when we have a new signed-in user
      // so the UI shows the skeleton state instead of a "no access" flash.
      if (nextId) setLoading(true);
      else setLoading(false);
    }
  }, [isSignedIn, clerkUser?.id]);

  const fetchPermissions = useCallback(async (expectedClerkId) => {
    // Pulled separately so a stale permissions cache can be busted on demand
    // (e.g. when a master admin reassigns this user's role).
    //
    // `expectedClerkId` is the Clerk user id that was active when the
    // fetch was scheduled. If Clerk has since swapped users, we DROP
    // the response instead of writing it to state — otherwise a slow
    // response for User A could land after User B has signed in and
    // grant User B User A's permissions.
    try {
      const res = await api.get("/permissions/catalog");
      if (activeClerkIdRef.current !== expectedClerkId) return; // stale
      setPermissions(new Set(res.data?.my_permissions || []));
      setPermissionsLoaded(true);
    } catch (e) {
      if (activeClerkIdRef.current !== expectedClerkId) return; // stale
      // Non-fatal — fall back to an empty permission set. Pages that gate
      // on hasPerm() will simply hide the feature rather than crash. We
      // still mark loaded=true so consumers know the fetch resolved
      // (they can distinguish "no perms" from "still loading").
      setPermissions(new Set());
      setPermissionsLoaded(true);
    }
  }, []);

  const fetchMe = useCallback(async () => {
    // Don't fire until Clerk has finished loading its initial state.
    if (!isLoaded) return null;
    if (!isSignedIn) {
      // The clerkUser-id effect above already cleared state; keep the
      // idempotent set here as belt-and-braces in case the ordering
      // shifts.
      setUser(null);
      setPermissions(new Set());
      setPermissionsLoaded(false);
      setLoading(false);
      return null;
    }
    if (fetchingRef.current) return null;
    fetchingRef.current = true;
    // Snapshot the Clerk user id at fetch-start so we can discard the
    // response if Clerk has swapped users by the time it lands.
    const expectedClerkId = clerkUser?.id ?? null;

    // Retry up to 3 times to absorb the brief window after sign-in where
    // Clerk's session token may not have propagated yet.
    let lastErr = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const res = await api.get("/auth/me");
        // If the Clerk session flipped to a different user while we
        // were awaiting the response, throw the result away — writing
        // it to state would let the previous user's role/is_master
        // leak into the new session.
        if (activeClerkIdRef.current !== expectedClerkId) {
          fetchingRef.current = false;
          return null;
        }
        setUser(res.data);
        // Fire-and-forget the permissions fetch — we don't block the
        // initial render on it, the UI just resolves features as it lands.
        fetchPermissions(expectedClerkId);
        setLoading(false);
        fetchingRef.current = false;
        return res.data;
      } catch (e) {
        lastErr = e;
        const status = e?.response?.status;
        // 401 is recoverable (token may not be ready yet). 403 means we're
        // signed in but rejected by the backend — no point retrying.
        if (status === 403) break;
        await new Promise((r) => setTimeout(r, 400));
      }
    }
    // eslint-disable-next-line no-console -- explicit error logging for auth diagnostics
    console.warn("[auth] /auth/me failed after retries:", lastErr?.response?.status, lastErr?.response?.data);
    if (activeClerkIdRef.current === expectedClerkId) {
      setUser(null);
      setPermissions(new Set());
      setPermissionsLoaded(true); // 401/403 = we KNOW they have nothing
      setLoading(false);
    }
    fetchingRef.current = false;
    return null;
  }, [isLoaded, isSignedIn, clerkUser?.id, fetchPermissions]);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  // Listen for the "your role changed, please re-authenticate" signal
  // dispatched by axios when it sees `X-Signout-Reason: role_changed`.
  //
  // ── Why the module-level gate ──
  // A single page load makes 5+ parallel API requests (auth/me,
  // permissions/catalog, dashboard/summary, daily-summary, weather,
  // etc.). If an admin flipped the current user's role mid-session,
  // every one of those requests will 401 with the same header and each
  // will dispatch this event. Without a gate we used to:
  //   1. schedule a setTimeout per event,
  //   2. call setUser(null) once each timer fired → triggering
  //      React re-renders through *every* <Navigate replace> in the
  //      route tree (AdminRoute, PermRoute, Protected),
  //   3. run clerkSignOut() several times in parallel.
  // Each of those steps calls history.replaceState(). Cumulatively
  // we blew past the browser's 100-per-10-seconds throttle → the
  // "SecurityError: Attempt to use history.replaceState() more than
  // 100 times per 10 seconds" that showed up in Sentry.
  //
  // Fix: gate on a module-scoped ref so ONLY the first event does the
  // work, hard-redirect via window.location.replace (no history
  // accumulation), and skip setUser(null) so we don't trigger the
  // re-render cascade of the route guards while sign-out is in flight.
  useEffect(() => {
    const onForceSignout = (ev) => {
      if (forceSignoutFiredRef.current) return;
      forceSignoutFiredRef.current = true;
      const message = ev?.detail?.message
        || "Your permissions have changed. Please sign in again.";
      toast.info(message, { duration: 4000 });
      // Kick off Clerk sign-out AND hard-redirect. We don't await
      // clerkSignOut() before redirecting because the browser tears
      // down JS on navigation anyway — the server-side session gets
      // cleaned up when the user hits Clerk's sign-in flow on /login.
      // `location.replace` (vs `.href = …`) avoids adding an entry
      // to the history stack, which prevents the user from
      // accidentally back-navigating into the broken state.
      try { clerkSignOut(); } catch (_) { /* noop */ }
      window.location.replace("/login?signed_out=role_changed");
    };
    window.addEventListener(FORCE_SIGNOUT_EVENT, onForceSignout);
    return () => window.removeEventListener(FORCE_SIGNOUT_EVENT, onForceSignout);
  }, [clerkSignOut]);

  const logout = async () => {
    try { await clerkSignOut(); } catch (e) { /* noop */ }
    setUser(null);
    window.location.href = "/login";
  };

  const isAdmin = user?.role === "admin" || user?.is_master === true;
  const isExecutive = user?.role === "executive";
  const isMaster = !!user?.is_master;

  // Defence-in-depth: even if `permissions`/`user` briefly hold a
  // previous user's data (should not happen after the wipe effect above,
  // but state updates in React are asynchronous), refuse to grant any
  // permission unless the loaded `user.clerk_user_id` matches the
  // current Clerk session id.
  const identityMatches =
    !!user &&
    !!clerkUser?.id &&
    (user.clerk_user_id === clerkUser.id);

  // Helper used across the app instead of role string-matching. `hasPerm`
  // returns true for master admins regardless of stored permissions.
  const hasPerm = useCallback(
    (key) => {
      if (!identityMatches) return false;
      return isMaster || permissions.has(key);
    },
    [identityMatches, isMaster, permissions]
  );

  // Public helper — callers pass no args; we resolve the currently-active
  // Clerk id under the hood so a stale response can't overwrite a fresh
  // user's permission set.
  const refreshPermissions = useCallback(
    () => fetchPermissions(activeClerkIdRef.current),
    [fetchPermissions]
  );

  return (
    <AuthContext.Provider value={{
      user, loading, logout, refresh: fetchMe,
      clerkUser, isAdmin: identityMatches && isAdmin,
      isExecutive: identityMatches && isExecutive,
      isMaster: identityMatches && isMaster,
      permissions, hasPerm, refreshPermissions,
      permissionsLoaded,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
